import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Utensils, Waves } from "lucide-react";
import { formatTRY, resolveImageUrl } from "@/lib/api";
import { track } from "@/lib/analytics";

const DISCOUNT_META = {
    early_booking: { icon: "🐦", label: "Erken Rez.", bg: "bg-fuchsia-500" },
    last_minute: { icon: "⏰", label: "Son Dakika", bg: "bg-rose-500" },
    long_stay: { icon: "🛏️", label: "Uzun Konaklama", bg: "bg-indigo-500" },
    date_range: { icon: "📅", label: "Sezon", bg: "bg-sky-500" },
    promo: { icon: "🎯", label: "Promo", bg: "bg-emerald-500" },
};

function DiscountBadge({ discount, compact = false }) {
    if (!discount) return null;
    const meta = DISCOUNT_META[discount.type] || DISCOUNT_META.promo;
    const valueLabel = discount.discount_type === "percentage"
        ? `%${Math.round(discount.discount_value)}`
        : `-${formatTRY(discount.discount_value)}`;
    return (
        <div data-testid={`discount-badge-${discount.id}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg ${meta.bg} text-white text-xs font-bold shadow-md`}>
            <span className="text-sm">{meta.icon}</span>
            <span>{compact ? valueLabel : `${meta.label} ${valueLabel}`}</span>
        </div>
    );
}

export default function HotelCard({ hotel, horizontal = false }) {
    const calDisc = hotel.calendar_discount;  // {original, final, percent, label, type}
    const fakeDiscount = calDisc?.percent || (hotel.old_price_from > hotel.price_from
        ? Math.round(((hotel.old_price_from - hotel.price_from) / hotel.old_price_from) * 100)
        : 0);
    const oldPrice = calDisc?.original || hotel.old_price_from;
    const savedAmount = calDisc ? (calDisc.original - calDisc.final) : 0;
    const topDiscount = hotel.top_discount;
    const unavailable = hotel.available === false;

    const handleClick = () => {
        track("hotel_click", { hotel_id: hotel.id, hotel_name: hotel.name });
    };

    if (horizontal) {
        return (
            <Link
                to={`/hotel/${hotel.id}`}
                onClick={handleClick}
                data-testid={`hotel-card-${hotel.id}`}
                className={`group flex flex-col md:flex-row overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hotel-card-hover ${unavailable ? "opacity-60 grayscale" : ""}`}
            >
                <div className="relative md:w-64 md:flex-shrink-0 overflow-hidden">
                    <img
                        src={resolveImageUrl(hotel.main_image)}
                        alt={hotel.name}
                        className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {topDiscount ? (
                        <div className="absolute top-3 left-3"><DiscountBadge discount={topDiscount} /></div>
                    ) : calDisc ? (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold shadow-lg">
                            %{calDisc.percent} İNDİRİM{calDisc.label ? ` · ${calDisc.label}` : ""}
                        </div>
                    ) : fakeDiscount > 0 && (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold">
                            %{fakeDiscount} İNDİRİM
                        </div>
                    )}
                    {hotel.active_discount_count > 1 && (
                        <div className="absolute top-12 left-3 px-1.5 py-0.5 rounded-md bg-white/90 text-fuchsia-600 text-[10px] font-bold">
                            +{hotel.active_discount_count - 1} kampanya
                        </div>
                    )}
                    {hotel.tags?.includes("Misafir Favorisi") && (
                        <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-white/95 text-slate-900 text-xs font-bold">
                            ⭐ Favori
                        </div>
                    )}
                </div>
                <div className="flex-1 flex flex-col md:flex-row p-5 gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-1">
                            {[...Array(hotel.stars)].map((_, i) => (
                                <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                            ))}
                        </div>
                        <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 dark:text-white leading-tight mb-1 line-clamp-2">
                            {hotel.name}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 mb-2">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{hotel.destination_name} · {hotel.region}</span>
                        </div>

                        {/* Auto-pulled hotel info — only real data from admin */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300 mt-2">
                            {(hotel.boards || []).slice(0, 3).map((b) => (
                                <span key={b} className="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400">
                                    <Utensils className="h-3.5 w-3.5" /> {b}
                                </span>
                            ))}
                            {hotel.beach_distance_m > 0 && (
                                <span className="inline-flex items-center gap-1">
                                    <Waves className="h-3.5 w-3.5 text-sky-500" /> Denize {hotel.beach_distance_m < 1000 ? `${hotel.beach_distance_m} m` : `${(hotel.beach_distance_m/1000).toFixed(1)} km`}
                                </span>
                            )}
                        </div>

                        {/* Amenities chips */}
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {(hotel.amenities || []).slice(0, 5).map((a) => (
                                <span key={a} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{a}</span>
                            ))}
                            {hotel.amenities && hotel.amenities.length > 5 && (
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 font-semibold">+{hotel.amenities.length - 5} özellik</span>
                            )}
                            {hotel.tags?.slice(0, 2).map((t) => (
                                <span key={t} className="text-[11px] px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 font-semibold">{t}</span>
                            ))}
                        </div>

                        {hotel.address && (
                            <div className="flex items-start gap-1 text-[11px] text-slate-400 dark:text-slate-500 mt-3 line-clamp-1">
                                <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                <span className="truncate">{hotel.address}</span>
                            </div>
                        )}
                    </div>
                    <div className="md:w-52 md:border-l md:border-slate-100 dark:md:border-slate-800 md:pl-4 flex md:flex-col items-end justify-between flex-shrink-0">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="px-2 py-1 rounded-lg bg-sky-500 text-white font-bold text-sm">{hotel.rating}</div>
                            <div className="text-xs text-slate-500 text-right">
                                <div className="font-semibold text-slate-700 dark:text-slate-200">Harika</div>
                                <div>{hotel.review_count} yorum</div>
                            </div>
                        </div>
                        {unavailable ? (
                            <div className="text-right w-full" data-testid={`hotel-unavailable-${hotel.id}`}>
                                <div className="inline-block px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold">Bu tarihlerde müsait değil</div>
                                {hotel.unavailable_reason && <div className="text-[11px] text-slate-500 mt-1">{hotel.unavailable_reason}</div>}
                            </div>
                        ) : (
                        <div className="text-right w-full">
                            {(calDisc || fakeDiscount > 0) && oldPrice && (
                                <div className="text-sm text-slate-400 line-through">{formatTRY(oldPrice)}</div>
                            )}
                            <div className="text-xl md:text-2xl font-display font-bold text-slate-900 dark:text-white whitespace-nowrap">{formatTRY(hotel.price_from)}</div>
                            <div className="text-xs text-slate-500 mb-2">gece / oda{calDisc && savedAmount > 0 ? <span className="block text-emerald-600 font-bold">{formatTRY(savedAmount)} tasarruf</span> : null}</div>
                            <span className="inline-block w-full text-center px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors">Detay</span>
                        </div>
                        )}
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link
            to={`/hotel/${hotel.id}`}
            onClick={handleClick}
            data-testid={`hotel-card-${hotel.id}`}
            className="group block rounded-2xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hotel-card-hover"
        >
            <div className="relative overflow-hidden aspect-[4/3]">
                <img src={resolveImageUrl(hotel.main_image)} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                {topDiscount ? (
                    <div className="absolute top-3 left-3"><DiscountBadge discount={topDiscount} /></div>
                ) : calDisc ? (
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold shadow-lg">
                        %{calDisc.percent} İNDİRİM{calDisc.label ? ` · ${calDisc.label}` : ""}
                    </div>
                ) : fakeDiscount > 0 && (
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold">
                        %{fakeDiscount} İNDİRİM
                    </div>
                )}
                {hotel.active_discount_count > 1 && (
                    <div className="absolute top-12 left-3 px-1.5 py-0.5 rounded-md bg-white/95 text-fuchsia-600 text-[10px] font-bold shadow">
                        +{hotel.active_discount_count - 1} kampanya
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="flex items-center gap-1 mb-1">
                    {[...Array(hotel.stars)].map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                    ))}
                </div>
                <h3 className="font-display font-bold text-slate-900 dark:text-white line-clamp-1 mb-1">{hotel.name}</h3>
                <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-3">
                    <MapPin className="h-3 w-3" />
                    <span>{hotel.destination_name}</span>
                </div>
                <div className="flex items-end justify-between">
                    <div className="flex items-center gap-2">
                        <div className="px-1.5 py-0.5 rounded-md bg-sky-500 text-white font-bold text-xs">{hotel.rating}</div>
                        <span className="text-xs text-slate-500">({hotel.review_count})</span>
                    </div>
                    <div className="text-right">
                        {(calDisc || fakeDiscount > 0) && oldPrice && (
                            <div className="text-xs text-slate-400 line-through">{formatTRY(oldPrice)}</div>
                        )}
                        <div className="text-lg font-display font-bold text-slate-900 dark:text-white">{formatTRY(hotel.price_from)}</div>
                        <div className="text-[10px] text-slate-500">gece{calDisc && savedAmount > 0 ? <span className="block text-emerald-600 font-bold">−{formatTRY(savedAmount)}</span> : null}</div>
                    </div>
                </div>
            </div>
        </Link>
    );
}
